import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router, RouterModule } from '@angular/router';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { CreateProctoringConfigurationPayload } from '@edtech-ui/core/models/proctoring-dashboard-models/create-proctoring-config';
import { ProctoringService } from '@edtech-ui/core/services/proctoring-dashboard/proctoring.service';
import { allowedDevicesBrowsersProto } from '@edtech-ui/core/utils/proctoring-dashboard/allowed devises.validators';
import { ProctoringConfigValidationUtils } from '@edtech-ui/core/utils/proctoring-dashboard/proctoring-config';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { DefultsRulesDialogBoxComponent } from '../defults-rules-dialog-box/defults-rules-dialog-box.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

@Component({
	selector: 'app-add-configurations',
	standalone: true,
	imports: [CommonModule, RouterModule, ReactiveFormsModule, MatProgressSpinnerModule,
		HasPermissionDirective, FormsModule, MatSlideToggleModule
	],
	templateUrl: './add-configurations.component.html',
	styleUrl: './add-configurations.component.scss'
})
export class AddConfigurationsComponent implements OnDestroy, OnInit {

	configForm: FormGroup;
	subscription = new Subscription();
	isLoading = false;
	companyId = '';
	PermissionForStudents = PermissionForStudents;
	violationEngine = true;
	autoTerminate = true;


	constructor(
		private router: Router,
		private proctoringService: ProctoringService,
		private toastr: ToastrService,
		private dialog: MatDialog
	) {
		this.configForm = new FormGroup({
			name: new FormControl('', [Validators.required, ProctoringConfigValidationUtils.configurationNameValidator()]),
			isEnabled: new FormControl(true),
			allowedBrowsers: new FormControl(['Chrome'], [allowedDevicesBrowsersProto()]),
			allowedDevices: new FormControl(['Laptop'], [allowedDevicesBrowsersProto()]),
			requireCamera: new FormControl(true),
			requireMicrophone: new FormControl(false),
			description: new FormControl('', [ProctoringConfigValidationUtils.proctoringDescriptionValidator()]),
			isDefault: new FormControl(false),
			requireScreenShare: new FormControl(false),
			requireFullscreen: new FormControl(false),
			recordCamera: new FormControl(true),
			recordScreen: new FormControl(false),
			recordAudio: new FormControl(false),
			enableServerAnalysis: new FormControl(false),
			enableResultCache: new FormControl(true),
			retentionDays: new FormControl(90),
			maxMedium: new FormControl(1, [Validators.required, ProctoringConfigValidationUtils.proctoringThresholdValidator()]),
			maxHigh: new FormControl(1, [Validators.required, ProctoringConfigValidationUtils.proctoringThresholdValidator()]),
			maxCritical: new FormControl(1, [Validators.required, ProctoringConfigValidationUtils.proctoringThresholdValidator()]),
			snapshotIntervalSeconds: new FormControl(30),
			heartbeatIntervalSeconds: new FormControl(10),
			maxSessionDurationSeconds: new FormControl(7200),
			apiBaseUrl: new FormControl(''),
			modelsUrl: new FormControl('')
		});
	}


	ngOnInit(): void {
		this.companyId = localStorage.getItem('company_id') || '';
		this.initializeFormSubscriptions();
	}


	browsers = [
		{ name: 'Chrome', selected: true },
		{ name: 'Edge', selected: false },
		{ name: 'Firefox', selected: false },
		{ name: 'Safari', selected: false }
	];


	devices = [
		{ name: 'Desktop', selected: false },
		{ name: 'Laptop', selected: true },
		// { name: 'Windows', selected: true },
		// { name: 'macOS', selected: true },
		// { name: 'Linux', selected: false },
		{ name: 'Tablet', selected: false },
		{ name: 'Mobile', selected: false }
	];


	toggleChip(item: any) {
		item.selected = !item.selected;
	}


	submitConfiguration() {
		if (this.configForm.invalid) {
			this.configForm.markAllAsTouched();
			return;
		}
		const payload: CreateProctoringConfigurationPayload = {
			...this.configForm.value,
			companyId: this.companyId
		}
		this.isLoading = true;
		this.subscription.add(
			this.proctoringService.postProctoringConfiguration(payload).pipe(
				tap((response) => {
					if (response.success) {
						this.toastr.success(response.message);
						this.router.navigate(['/proctoring-dashboard/proto-configs']);
					} else {
						this.toastr.error(response.message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					return of(null);
				})
			).subscribe()
		);
	}


	toggleBrowser(browser: string) {
		const selected = [...this.configForm.value.allowedBrowsers];
		const index = selected.indexOf(browser);
		if (index > -1) {
			selected.splice(index, 1);
		} else {
			selected.push(browser);
		}
		this.configForm.patchValue({ allowedBrowsers: selected });
		this.configForm.get('allowedBrowsers')?.markAsTouched();
		this.configForm.get('allowedBrowsers')?.updateValueAndValidity();
	}


	toggleDevice(device: string) {
		const selected = [...this.configForm.value.allowedDevices];
		const index = selected.indexOf(device);
		if (index > -1) {
			selected.splice(index, 1);
		} else {
			selected.push(device);
		}
		this.configForm.patchValue({ allowedDevices: selected });
		this.configForm.get('allowedDevices')?.markAsTouched();
		this.configForm.get('allowedDevices')?.updateValueAndValidity();
	}



	private initializeFormSubscriptions(): void {
		this.subscription.add(
			this.configForm.get('requireCamera')!.valueChanges.subscribe(value => {
				if (!value) {
					this.configForm.patchValue({ recordCamera: false });
				}
			})
		);

		this.subscription.add(
			this.configForm.get('requireMicrophone')!.valueChanges.subscribe(value => {
				if (!value) {
					this.configForm.patchValue({ recordAudio: false });
				}
			})
		);

		this.subscription.add(
			this.configForm.get('requireScreenShare')!.valueChanges.subscribe(value => {
				if (!value) {
					this.configForm.patchValue({
						recordScreen: false,
						screenMonitoring: false,
						screenshotCapture: false
					});
				}
			})
		);

		this.subscription.add(
			this.configForm.get('recordScreen')!.valueChanges.subscribe(value => {
				if (!value) {
					this.configForm.patchValue({
						screenMonitoring: false,
						screenshotCapture: false
					});
				}
			})
		);
	}


	openDefaultsDialogBox(): void {
		const dialogRef = this.dialog.open(DefultsRulesDialogBoxComponent, {
			width: '500px',
			disableClose: true
		});

		dialogRef.afterClosed().subscribe((result: boolean) => {
			if (result) {
				this.configForm.patchValue({
					isDefault: true
				});
			}
		});
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}
